import { Base64 } from 'js-base64';
import { AxiosError } from 'axios';
import api from 'api';
import { useDispatch } from 'hooks';
import { actions } from 'store';
import {
  addVideoEntry,
  clearAllEntries,
  tracker,
  uploadAllEntries
} from 'lib/reader/analytics';
import { analyticsLocalStorage } from '.';

import type { Course, RequestUser, ServerConfig } from 'shared-types';

export type ContextualizedReaderTracker = ReturnType<
  typeof trackerContextualized
>;

interface Params {
  chapterId: number;
  chapterSlug: string;
  courseData: Pick<
    Course,
    | 'id'
    | 'completionEnabled'
    | 'disableAllAdminAnalytics'
    | 'enableAdminAnalytics'
    | 'slug'
  > &
    Record<
      | 'isSuperAdmin'
      | 'isCourseOwner'
      | 'isCourseAdmin'
      | 'isAddedOrGroupOrPathUser',
      boolean
    >;
  dispatch: ReturnType<typeof useDispatch>['dispatch'];
  onErrorMessage: string;
  timeZone: ServerConfig['timeZone'];
  user?: RequestUser;
}

const trackerContextualized = ({
  chapterId,
  chapterSlug,
  courseData,
  dispatch,
  onErrorMessage,
  timeZone,
  user
}: Params) => {
  let isTracking = false;

  const clearEntries = ({ courseId }: { courseId: number }) => {
    clearAllEntries({ courseId });
  };

  const getStatus = () => {
    return { isTracking, chapter: chapterSlug };
  };

  const isUserTrackable = () => {
    if (
      !user ||
      user?.roles.includes('vuoadmin') ||
      ((courseData.isSuperAdmin ||
        courseData.isCourseOwner ||
        courseData.isCourseAdmin) &&
        !courseData.isAddedOrGroupOrPathUser &&
        !courseData.enableAdminAnalytics) ||
      (user?.roles.includes('admin') &&
        !courseData.isAddedOrGroupOrPathUser &&
        courseData.disableAllAdminAnalytics)
    ) {
      return false;
    }
    return true;
  };

  const init = (hOffset = 0) => {
    if (!isUserTrackable()) return;

    tracker.initTracking({
      courseId: courseData.id,
      chapterId,
      hOffset,
      userId: user?.id,
      timeZone
    });
  };

  const stop = () => {
    tracker.stopTracking({
      courseId: courseData.id,
      chapterId,
      userId: user?.id,
      timeZone
    });
    isTracking = false;
    return true;
  };

  const track = () => {
    if (!isUserTrackable()) return;

    tracker.trackBlocks({
      courseId: courseData.id,
      chapterId,
      userId: user?.id,
      timeZone
    });
    isTracking = true;
  };

  const trackVideoTick = ({
    blockId,
    steps,
    tickEnd,
    tickStart
  }: {
    blockId: number;
    steps: number[];
    tickEnd: number;
    tickStart: number;
  }) => {
    if (!isUserTrackable()) return;

    let startStep = 0;
    let endStep = 0;
    for (let i = 0; i < steps.length; i++) {
      if (tickStart > startStep && tickStart < steps[i]) {
        startStep = steps[i];
      }
      if (tickEnd > endStep && tickEnd >= steps[i]) {
        endStep = steps[i];
      } else if (i === steps.length - 1 && tickEnd + 1 > steps[i]) {
        // because of inconsistencies in calculating video length we might be at
        // the end even thought end does not match the last step exactly
        endStep = steps[i];
      }
    }

    const viewTimes = {};
    for (let i = 0; i < steps.length; i++) {
      const time = steps[i];
      viewTimes[time] = time >= startStep && time <= endStep ? 1 : 0;
    }

    const totalViewTime = Math.round(tickEnd) - Math.round(tickStart);

    addVideoEntry({
      chapterId: chapterId,
      courseId: courseData.id,
      currentTimestamp: tickEnd,
      userId: user.id,
      blockId: blockId,
      viewTimes: viewTimes,
      totalViewTime: totalViewTime
    });
  };

  const update = async () => {
    await uploadAllEntries();
    if (!courseData.completionEnabled) return;

    try {
      const response = await api.get(
        `/reader/courses/${courseData.slug}/progress`
      );
      dispatch(
        actions.updatePageState({ progressData: response.data.progressData })
      );
    } catch (err) {
      if (err && err instanceof AxiosError && err.response?.status === 401) {
        // do nothing, as these are triggered by SCORM files
        // when the user session has expired
      } else await dispatch.handleErrorDispatch(err, onErrorMessage);
    }
  };

  if (!isUserTrackable() && user) {
    // check if any analytics key belongs to the user
    // that we no longer should track and remove the
    // entry so it is not uploaded to the server
    const keys = analyticsLocalStorage.getKeys();

    keys.forEach((key) => {
      if (!key) return;

      const value = localStorage.getItem(key);
      const decodedValue = JSON.parse(Base64.decode(value));

      if (
        decodedValue?.courseId === courseData.id &&
        decodedValue?.userId === user.id
      ) {
        localStorage.removeItem(key);
      }
    });
  }

  return {
    clearEntries,
    getStatus,
    isUserTrackable,
    init,
    stop,
    track,
    trackVideoTick,
    update
  };
};

export default trackerContextualized;
